import { createMapViewScroller } from '../../shared/map-view-scroll';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Statuses } from '../../shared/constants/status.constants';
import { changeUserSort, changeUserPage, setUserMapView } from '../../store/actions/users.action';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import {
  selectPagedUsers,
  selectTotalUsers,
  selectUserPagination,
  selectVisibleUserPages,
  selectUserSort,
  selectAllUsers,
  selectIsUserMapView,
} from '../../store/selectors/users.selectors';
import { PaginationState } from '../../store/states/car.state';
import { MapParameters } from '../../models/map-parameters.model';
import { MapResultComponent } from '../../shared/components/map-result/map-result.component';

type SortableUserProperty = 'name' | 'email' | 'createdAt' | 'carCount' | 'realEstateCount';

@Component({
  selector: 'app-user-search-result',
  standalone: false,
  templateUrl: './user-search-result.component.html',
  styleUrl: './user-search-result.component.scss',
})
export class UserSearchResultComponent implements AfterViewInit, OnDestroy {
  @Output() mapChanged = new EventEmitter<MapParameters>();
  @Output() onListView = new EventEmitter<void>();

  @ViewChild('mapToggle') private mapToggle?: ElementRef<HTMLElement>;
  private readonly mapViewScroll = createMapViewScroller(
    () => this.mapToggle?.nativeElement,
    () => this.mapMode()
  );

  @ViewChild(MapResultComponent) mapResultComponent!: MapResultComponent;

  @Input() mapParameters?: MapParameters;

  sortProperty = '';
  sortDirection: 'asc' | 'desc' | null = 'asc';
  sortLabel = '';
  sortIcon = '';

  private router = inject(Router);
  private store = inject(Store);

  private readonly mapMode = this.store.selectSignal(selectIsUserMapView);
  private mapTabListener: any;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);
  users$ = this.store.select(selectPagedUsers);
  total$ = this.store.select(selectTotalUsers);
  pagination$ = this.store.select(selectUserPagination);
  pages$ = this.store.select(selectVisibleUserPages);
  userState$ = this.store.select(selectUserSort);
  allUsers$ = this.store.select(selectAllUsers);
  isMapView$ = this.store.select(selectIsUserMapView);

  statuses = Statuses;

  private destroy$ = new Subject<void>();

  constructor() {
    this.userState$.pipe(takeUntil(this.destroy$)).subscribe((userSort) => {
      this.sortDirection = userSort.direction;
      this.sortProperty = userSort.active as string;
      this.setSortIcon();
      switch (userSort.active) {
        case 'createdAt':
          this.sortLabel = 'ORDER_BY_USER_DATE';
          break;
        case 'name':
          this.sortLabel = 'ORDER_BY_NAME';
          break;
        case 'email':
          this.sortLabel = 'ORDER_BY_EMAIL';
          break;
        case 'carCount':
          this.sortLabel = 'ORDER_BY_CAR_COUNT';
          break;
        case 'realEstateCount':
          this.sortLabel = 'ORDER_BY_PROPERTY_COUNT';
          break;
        default:
          this.sortLabel = 'ORDER_BY';
      }
    });
  }

  ngAfterViewInit(): void {
    const mapTabEl = document.getElementById('map-tab');

    if (mapTabEl) {
      this.mapTabListener = mapTabEl.addEventListener('shown.bs.tab', () => {
        this.onMapTabActivated();
      });
    }
  }

  setMapView(isMap: boolean): void {
    const enteringMap = isMap && !this.mapMode();
    this.mapViewScroll.cancel();
    this.store.dispatch(setUserMapView({ isUserMapView: isMap }));
    this.onListView.emit();
    if (enteringMap) this.mapViewScroll.request();
  }

  onMapTabActivated(): void {
    this.mapResultComponent?.invalidateMap();
  }

  updateMap(event: MapParameters) {
    this.mapChanged.emit(event);
  }

  setSortIcon(): void {
    if (!this.sortProperty) {
      this.sortIcon = 'bi-filter-square-fill';
    }

    this.sortIcon = this.sortDirection === 'asc' ? 'bi-sort-up' : 'bi-sort-down';
  }

  goToDetails(userId: number): void {
    this.router.navigate(['/users', userId]);
  }

  setSort(property: SortableUserProperty): void {
    const direction = this.getDefaultSortingDirection(property);
    this.store.dispatch(changeUserSort({ active: property, direction: direction }));
  }

  getDefaultSortingDirection(property: SortableUserProperty): 'asc' | 'desc' {
    if (property === this.sortProperty) {
      const direction = this.sortDirection === 'asc' ? 'desc' : 'asc';

      return direction;
    }
    return 'asc';
  }

  goTo(page: number): void {
    this.store.dispatch(changeUserPage({ pageIndex: page - 1 }));
  }

  first(): void {
    this.store.dispatch(changeUserPage({ pageIndex: 0 }));
  }

  last(total: number, pageSize: number): void {
    const lastPage = Math.ceil(total / pageSize) - 1;
    this.store.dispatch(changeUserPage({ pageIndex: lastPage }));
  }

  isLastPage(total: number, pagination: PaginationState): boolean {
    let isLast = false;
    isLast = pagination.pageIndex >= Math.ceil(total / pagination.pageSize) - 1;
    return isLast;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
